import { createContext, useContext, useSyncExternalStore } from 'react'

export type Motion = {
  /** True when animations should hold their final state: the visitor paused them or prefers reduced motion. */
  paused: boolean
  /** The system "reduce motion" setting. */
  reduceMotion: boolean
  setPaused: (paused: boolean) => void
}

export const MotionContext = createContext<Motion>({ paused: false, reduceMotion: false, setPaused: () => {} })

export const useMotion = () => useContext(MotionContext)

const reducedMotionQuery = '(prefers-reduced-motion: reduce)'

const subscribeToReducedMotion = (onChange: () => void) => {
  const query = window.matchMedia?.(reducedMotionQuery)
  query?.addEventListener('change', onChange)
  return () => query?.removeEventListener('change', onChange)
}

export const usePrefersReducedMotion = () =>
  useSyncExternalStore(
    subscribeToReducedMotion,
    () => window.matchMedia?.(reducedMotionQuery).matches ?? false,
    () => false,
  )

/* The visitor's own pause choice, remembered across visits. null means no choice yet, so motion
   follows the system setting. */
const storageKey = 'ua-motion-paused'
const choiceListeners = new Set<() => void>()
let storedChoice: boolean | null | undefined

const readChoice = () => {
  if (storedChoice === undefined) {
    try {
      const value = window.localStorage.getItem(storageKey)
      storedChoice = value === null ? null : value === 'true'
    } catch {
      storedChoice = null
    }
  }
  return storedChoice
}

export const writeMotionChoice = (paused: boolean) => {
  storedChoice = paused
  try {
    window.localStorage.setItem(storageKey, String(paused))
  } catch {
    // Storage unavailable (private mode, blocked): the choice holds for this visit only.
  }
  choiceListeners.forEach((listener) => listener())
}

const subscribeToChoice = (listener: () => void) => {
  choiceListeners.add(listener)
  return () => {
    choiceListeners.delete(listener)
  }
}

export const useMotionChoice = () => useSyncExternalStore(subscribeToChoice, readChoice, () => null)

const subscribeToNothing = () => () => {}

/** False while rendering on the server and hydrating, true after: client-only behaviour switches on here. */
export const useIsClient = () => useSyncExternalStore(subscribeToNothing, () => true, () => false)
